import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/api';
import { Briefcase, MapPin, Clock, Loader2, ChevronRight, Search } from 'lucide-react';

const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 20000 });

const OG_IMAGE = '/og-werken-bij.png';
const CAREERS_DESC = 'Bekijk de openstaande vacatures bij Truckwash1 en solliciteer direct. Geen account nodig.';

const TYPE_LABEL = { vast: 'Vast dienstverband', tijdelijk: 'Tijdelijk', oproep: 'Oproepkracht' };

function VacatureCard({ v }) {
  return (
    <Link
      to={`/werken-bij/${v.id}`}
      data-testid={`careers-card-${v.id}`}
      className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-all hover:border-tw1-accent-gold/60 hover:bg-white/[0.07]"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-white">{v.title}</h3>
        <span className="mt-1 shrink-0 rounded-full bg-tw1-accent-gold/15 px-2.5 py-0.5 text-[11px] font-medium text-tw1-accent-gold">
          {TYPE_LABEL[v.employment_type] || v.employment_type || 'Vacature'}
        </span>
      </div>
      {v.functie && <p className="mt-1 text-sm text-white/60">{v.functie}</p>}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-white/70">
        {(v.location_name || v.plaats) && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={14} className="text-tw1-accent-gold" /> {v.location_name || v.plaats}
          </span>
        )}
        {v.hours != null && (
          <span className="inline-flex items-center gap-1.5">
            <Clock size={14} className="text-tw1-accent-gold" /> {v.hours} uur p/w
          </span>
        )}
      </div>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-tw1-accent-gold">
        Bekijk & solliciteer
        <ChevronRight size={15} className="transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

export default function PublicVacatures() {
  const [vacatures, setVacatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    document.head.querySelectorAll('[data-default-seo]').forEach((el) => el.remove());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get('/public/vacatures');
        setVacatures(data.vacatures || []);
      } catch (e) {
        setError('Kon de vacatures niet laden. Probeer het later opnieuw.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? vacatures.filter((v) =>
          [v.title, v.functie, v.location_name].filter(Boolean).join(' ').toLowerCase().includes(q))
      : vacatures;
    const byLoc = {};
    for (const v of filtered) {
      const key = v.location_name || v.plaats || 'Overig';
      (byLoc[key] ||= []).push(v);
    }
    return Object.entries(byLoc).sort((a, b) => a[0].localeCompare(b[0], 'nl'));
  }, [vacatures, query]);

  const total = vacatures.length;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = origin + (typeof window !== 'undefined' ? window.location.pathname : '/werken-bij');
  const ogImageUrl = origin + OG_IMAGE;
  const title = 'Werken bij Truckwash1 — Vacatures';

  return (
    <div className="min-h-screen bg-tw1-brand-navy" data-testid="public-vacatures-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={CAREERS_DESC} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Truckwash1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={CAREERS_DESC} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={CAREERS_DESC} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      <header className="border-b border-white/10 px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center gap-3">
          <img src="/images/logo.png" alt="Truckwash1" className="h-8 w-auto object-contain" />
          <div className="h-6 w-px bg-white/20" />
          <span className="text-sm font-medium text-white/80">Werken bij Truckwash1</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="max-w-2xl">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-tw1-accent-gold">Vacatures</p>
          <h1 className="mt-3 text-4xl font-bold text-white sm:text-5xl">Werken bij Truckwash1</h1>
          <p className="mt-4 text-base leading-relaxed text-white/70">
            Word onderdeel van het team dat dagelijks vrachtwagens spik en span maakt.
            Bekijk onze openstaande vacatures en solliciteer direct — geen account nodig.
          </p>
        </div>

        {!loading && total > 0 && (
          <div className="mt-8 flex items-center gap-3">
            <div className="relative w-full max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="careers-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek op functie of locatie…"
                className="w-full rounded-lg border border-white/15 bg-white/[0.05] py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:border-tw1-accent-gold focus:outline-none"
              />
            </div>
            <span className="text-sm text-white/50">{total} vacature{total === 1 ? '' : 's'}</span>
          </div>
        )}

        <div className="mt-10">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-white/60" data-testid="careers-loading">
              <Loader2 size={20} className="mr-2 animate-spin" /> Vacatures laden…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-8 text-center text-white/70" data-testid="careers-error">
              {error}
            </div>
          ) : grouped.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-12 text-center" data-testid="careers-empty">
              <Briefcase size={36} className="mx-auto mb-3 text-white/30" />
              <p className="text-lg font-medium text-white">Op dit moment geen openstaande vacatures</p>
              <p className="mt-1 text-sm text-white/60">
                Kom binnenkort terug — er komen regelmatig nieuwe kansen bij.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {grouped.map(([loc, list]) => (
                <section key={loc} data-testid={`careers-group-${loc}`}>
                  <div className="mb-4 flex items-center gap-2">
                    <MapPin size={16} className="text-tw1-accent-gold" />
                    <h2 className="text-lg font-semibold text-white">{loc}</h2>
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/60">{list.length}</span>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {list.map((v) => <VacatureCard key={v.id} v={v} />)}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 px-6 py-6">
        <p className="mx-auto max-w-5xl text-[12px] text-white/40">
          © {new Date().getFullYear()} Truckwash1 Group · Werken bij ons
        </p>
      </footer>
    </div>
  );
}
